import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserActions, useUserAuthData } from 'entities/User';
import { Button, Typography } from 'shared/_ui';
import { getRouteRussiaLogin } from 'shared/consts/routes';
import { useCreateSetupManagementLink } from '../api/setupManagementApi';
import { validateSetupManagementLink } from '../model/managementLink';
import styles from './styles.module.scss';

export const SetupManagement: React.FC<{ token: string }> = ({ token }) => {
  const { t } = useTranslation();
  const sessionToken = useUserAuthData()?.token;
  const { logout } = useUserActions();
  const [createLink, { reset }] = useCreateSetupManagementLink();
  const [link, setLink] = useState<{ url: string; sessionToken: string }>();
  const [failure, setFailure] = useState<'signIn' | 'unavailable'>();
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  const pending = useRef<ReturnType<typeof createLink>>();

  useEffect(() => {
    setLink(undefined);
    setFailure(undefined);
    setLoading(false);
    return () => {
      generation.current += 1;
      pending.current?.abort();
      pending.current = undefined;
      reset();
    };
  }, [sessionToken, token, reset]);

  const prepareLink = async () => {
    if (!sessionToken || pending.current) return;
    const requestGeneration = generation.current;
    setLoading(true);
    setFailure(undefined);
    const request = createLink({ token });
    pending.current = request;
    try {
      const result = await request.unwrap();
      if (generation.current !== requestGeneration) return;
      const url = validateSetupManagementLink(result.telegramBindUrl);
      if (!url) {
        setFailure('unavailable');
        return;
      }
      setLink({ url, sessionToken });
    } catch (error: unknown) {
      if (generation.current !== requestGeneration) return;
      const status =
        error && typeof error === 'object' && 'status' in error
          ? error.status
          : undefined;
      setFailure(
        status === 401 || status === 403 || status === 404 || status === 410
          ? 'signIn'
          : 'unavailable'
      );
    } finally {
      if (generation.current === requestGeneration) {
        pending.current = undefined;
        setLoading(false);
        reset();
      }
    }
  };

  const readyLink =
    sessionToken && link?.sessionToken === sessionToken ? link.url : undefined;
  const needsSignIn = !sessionToken || failure === 'signIn';

  return (
    <section className={styles.card} aria-label={t('setup.management.title')}>
      <Typography.Title level={3}>
        {t('setup.management.title')}
      </Typography.Title>
      <Typography.Text>{t('setup.management.description')}</Typography.Text>
      <Typography.Text type="secondary">
        {t('setup.management.emailHint')}
      </Typography.Text>
      {failure && <p role="status">{t(`setup.management.${failure}`)}</p>}
      {needsSignIn ? (
        <>
          {!failure && (
            <Typography.Text>{t('setup.management.signIn')}</Typography.Text>
          )}
          <a
            className={styles.recoveryAction}
            href={getRouteRussiaLogin()}
            onClick={() => {
              if (sessionToken) logout();
            }}
          >
            {t(
              sessionToken
                ? 'setup.management.switchAccountAction'
                : 'setup.management.signInAction'
            )}
          </a>
        </>
      ) : readyLink ? (
        <>
          <p role="status">{t('setup.management.ready')}</p>
          <a
            className={styles.recoveryAction}
            href={readyLink}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
          >
            {t('setup.management.openBot')}
          </a>
        </>
      ) : (
        <Button
          type="primary"
          loading={loading}
          onClick={() => {
            void prepareLink();
          }}
        >
          {t(
            loading ? 'setup.management.preparing' : 'setup.management.openBot'
          )}
        </Button>
      )}
    </section>
  );
};
